import * as _ from "lodash";
import { getLabel } from "bpmn-js/lib/features/label-editing/LabelUtil";
import ElementRegistry from "diagram-js/lib/core/ElementRegistry";
import {
    map,
    filter,
    sortBy,

} from 'min-dash';
import { FrendsElementStore } from "../FrendsElementStore";
import { ElementType, IElementDictionary, IParameters } from "../IProcessData";
import { Base } from "diagram-js/lib/model";

/**
 * Provides ability to search for BPMN elements.
 * extended from https://github.com/bpmn-io/bpmn-js/blob/a88e059da574be9ac0de09ce1d011e8909bc86b6/lib/features/search/BpmnSearchProvider.js
 */
export default class BpmnSearchProvider {
    public _elementRegistry: any;
    public _canvas: any;
    public _frendsStore: IElementDictionary | null | undefined;

    constructor(elementRegistry: ElementRegistry, searchPad: any, canvas: any, frendsStore: FrendsElementStore) {
        this._elementRegistry = elementRegistry;
        this._canvas = canvas;
        this._frendsStore = frendsStore.getElements();
        searchPad.registerProvider(this);
    }

    public find(pattern: string) {
        const rootElement = this._canvas.getRootElement();
        let elements = this._elementRegistry.filter(function (element: any) {
            if (element.labelTarget) {
                return false;
            }
            return true;
        });

        // do not include root element
        elements = filter(elements, function (element: unknown) {
            return element !== rootElement;
        });

        const frendsStore = this._frendsStore;
        elements = map(elements, function (element: Base) {
            const elementParameterValueMatchTokens = BpmnSearchProvider.getElementParameterValues(element.id, pattern, frendsStore);
            const triggerParameterValueMatchTokens = BpmnSearchProvider.getTriggerParameterValues(element.id, pattern, frendsStore);
            const labelMatchTokens = BpmnSearchProvider.matchAndSplit(getLabel(element), pattern)

            const primaryTokens = [...elementParameterValueMatchTokens, ...triggerParameterValueMatchTokens, ...labelMatchTokens];
            return {
                primaryTokens: primaryTokens,
                secondaryTokens: BpmnSearchProvider.matchAndSplit(element.id, pattern),
                element: element
            };
        });

        // exclude non-matched elements
        elements = filter(elements, function (element: any) {
            return BpmnSearchProvider.hasMatched(element.primaryTokens) || BpmnSearchProvider.hasMatched(element.secondaryTokens);
        });

        elements = sortBy(elements, function (element: any) {
            return getLabel(element.element) + element.element.id;
        });

        return elements;
    }

    private static getElementParameterValues(elementId: string, searchPattern: string, frendsStore: IElementDictionary | null | undefined) {
        const parameters = frendsStore != null ? frendsStore[elementId]?.parameters : {}
        if (_.isEmpty(parameters)) {
            return [];
        }

        const parameterValues = BpmnSearchProvider.findValuesDeep(parameters)
        return BpmnSearchProvider.getAndMatchTokens(parameterValues, searchPattern);
    }

    private static getTriggerParameterValues(elementId: string, searchPattern: string, frendsStore: IElementDictionary | null | undefined) {
        const isTrigger = frendsStore != null && frendsStore[elementId]?.type === ElementType.Start;
        const parameters = isTrigger ? frendsStore[elementId]?.parameters : {}
        if (_.isEmpty(parameters)) {
            return [];
        }

        const parameterValues = Object.values(parameters)
        return BpmnSearchProvider.getAndMatchTokens(parameterValues, searchPattern);
    }

    private static getAndMatchTokens(parameterValues: unknown[], searchPattern: string){
        const tokens = parameterValues.flatMap((parameter) => {
            if (_.isNil(parameter) || _.isEmpty(parameter)) {
                return [];
            }
            return BpmnSearchProvider.matchToken(String(parameter), searchPattern);

        })
        return tokens;
    }

    // Recursive method to find values in Tasks because they can be nested
    private static findValuesDeep(obj: IParameters): string[] {
        let values: string[] = [];

        for (const key in obj) {
            if (typeof obj[key] === "object" && obj[key] !== null) {
                values = values.concat(BpmnSearchProvider.findValuesDeep(obj[key]));
            } else if (key === "value") {
                values.push(obj[key]);
            }
        }

        return values;
    }

    /**
     * @param {Token[]} tokens
     *
     * @return {boolean}
     */
    private static hasMatched(tokens: Token[]) {
        const matched = filter(tokens, function (token: Token) {
            return !!token.matched;
        });

        return matched.length > 0;
    }

    /**
     * @param {string} elementLabel
     * @param {string} searchPattern
     *
     * @return {Token[]}
     */
    private static matchAndSplit(elementLabel: string, searchPattern: string) {
        if (!elementLabel) {
            return [];
        }

        const tokens = BpmnSearchProvider.matchToken(elementLabel, searchPattern)
        return tokens;
    }

    private static matchToken(valueToSearch: string, searchPattern: string) {
        const tokens: Token[] = [];

        valueToSearch = valueToSearch.toLowerCase();
        searchPattern = searchPattern.toLowerCase();

        // Find the index of the searchPattern in the elementLabel
        const patternIndex = valueToSearch.indexOf(searchPattern);

        // If the searchPattern is found in the valueToSearch
        if (patternIndex > -1) {
            // Maximum number of characters to show before and after the searchPattern
            const characterCap = 50;
            // If the searchPattern is not at the start of the valueToSearch, add the preceding part as a normal token
            if (patternIndex !== 0) {
                const start = Math.max(0, patternIndex - characterCap);
                tokens.push({
                    normal: valueToSearch.substring(start, patternIndex)
                });
            }

            // Add the matched part as a matched token
            tokens.push({
                matched: valueToSearch.substring(patternIndex, patternIndex + searchPattern.length)
            });

            // If there is a part following the searchPattern in the valueToSearch, add it as a normal token
            if (searchPattern.length + patternIndex < valueToSearch.length) {
                const end = Math.min(valueToSearch.length, patternIndex + searchPattern.length + characterCap);
                tokens.push({
                    normal: valueToSearch.substring(patternIndex + searchPattern.length, end)
                });
            }
        }

        return tokens;
    }

}

interface Token {
    normal?: string;
    matched?: string;
}

BpmnSearchProvider.$inject = [
    "elementRegistry",
    "searchPad",
    "canvas",
    "frendsStore"
];